import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';
import { updateChartTotals, setActiveStepper } from './shared';

// Module-level state
let currentDate: string = '';
let converting = false;    // km/miles conversion guard (run cards)
let newCardCounter = 0;
let hiddenListenerAttached = false;
let modalListenersBound = false;

interface RunData {
    runs_id: number;
    km: number | null;
    miles: number | null;
    time: string | null;
    incline: number | null;
}

export function initRunsModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }

    if (!modalListenersBound) {
        modalListenersBound = true;

        // D-03: autofocus the first field of the first card once the modal is actually
        // shown. Self-selects via `.run-card` — unique to this modal (stairmaster uses
        // `.sm-card`) — so this is a no-op when a different modal type is rendered.
        modalEl.addEventListener('shown.bs.modal', () => {
            const firstField = modalEl.querySelector(
                '#chart-edit-modal-body .run-card .js-run-km'
            ) as HTMLInputElement | null;
            firstField?.focus();
        });

        // D-04/D-05: container-level Enter-to-save. Falls through for <button> targets
        // ("+ Run" / per-card delete) so their native action is preserved. Self-selects via
        // `data-modal-type="runs"` — `.js-activity-save-all` is SHARED between runs and
        // stairmaster (same class name in both files), so the class alone would ambiguously
        // match whichever of the two is currently open; the data attribute disambiguates.
        modalEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON') return;
            const saveAllBtn = modalEl.querySelector(
                '.js-activity-save-all[data-modal-type="runs"]'
            ) as HTMLButtonElement | null;
            if (!saveAllBtn) return;
            e.preventDefault();
            handleSaveAll(saveAllBtn);
        });
    }
}

/**
 * Builds the activity-cards container + per-card skeleton/data + Add Run/Save All controls into
 * the already-cleared body (Phase 262, HOME-01/02). `runs === null` renders the D-01 show-first
 * blank skeleton card; an empty array renders the same blank-card empty state (a step that
 * resolved to zero runs); a populated array renders one real card per run. Returns the new
 * container element.
 */
function buildRunsBody(runs: RunData[] | null): HTMLElement {
    const body = document.getElementById('chart-edit-modal-body')!;

    const container = document.createElement('div');
    container.className = 'activity-cards-container';
    body.appendChild(container);

    if (runs === null || runs.length === 0) {
        // Skeleton: one real, enabled, blank run card so D-03's autofocus target (the first
        // field of the first card) is real and focusable from the very first paint.
        renderRunCard(container, null);
    } else {
        runs.forEach(run => renderRunCard(container, run));
    }

    // Add button row -- runs only
    const addRow = document.createElement('div');
    addRow.className = 'd-flex gap-2 mt-2';

    const addRunBtn = document.createElement('button');
    addRunBtn.type = 'button';
    addRunBtn.className = 'btn btn-outline-primary flex-fill';
    addRunBtn.textContent = '+ Run';
    addRunBtn.addEventListener('click', () => {
        const card = renderRunCard(container, null);
        container.appendChild(card);
        card.scrollIntoView({ behavior: 'smooth' });
    });

    addRow.appendChild(addRunBtn);
    body.appendChild(addRow);

    // Save All button. `data-modal-type="runs"` disambiguates from stairmaster-modal.ts's
    // Save All button, which shares the same `.js-activity-save-all` class name -- the
    // keydown listener above needs a reliable way to tell them apart.
    const saveAllBtn = document.createElement('button');
    saveAllBtn.type = 'button';
    saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-activity-save-all';
    saveAllBtn.dataset.modalType = 'runs';
    saveAllBtn.innerHTML = '<span class="js-activity-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-activity-spinner" role="status"></span>';
    saveAllBtn.addEventListener('click', () => handleSaveAll(saveAllBtn));
    body.appendChild(saveAllBtn);

    return container;
}

/**
 * Fetch-and-populate half of openRunsModal() (Phase 262, HOME-01/02) -- extracted so the
 * date-nav step path can reload the body for a new date without closing/reopening the modal.
 *
 * Open path (blankUntilLoaded=false): behaves exactly as before -- the body is cleared and the
 * D-01 show-first skeleton card + Add Run/Save All controls are built synchronously, then the
 * first card is populated in place once getRunsFromDate resolves (never rebuilt, so a
 * shown.bs.modal focus already landed on it survives).
 *
 * Step path (blankUntilLoaded=true): the body is cleared and left EMPTY (P-04 -- no skeleton
 * card, no spinner) until the fetch resolves, then the whole container/cards/controls are built
 * fresh from the response via buildRunsBody().
 */
function renderBodyForDate(date: string, blankUntilLoaded: boolean, done?: (ok: boolean) => void): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) { done?.(false); return; }
    body.innerHTML = '';

    let openContainer: HTMLElement | null = null;
    if (!blankUntilLoaded) {
        openContainer = buildRunsBody(null);
    }

    get('?do=getRunsFromDate&date=' + date, {}, (data: any) => {
        if (blankUntilLoaded && data.status !== 1) { done?.(false); return; }

        // CR-01 stale-response guard (mirrors mood-modal.ts:207). Two getRunsFromDate requests can
        // be in flight for different dates -- openModal()'s own fetch is not covered by date-nav's
        // _busy lock. Dropping the stale one prevents both the wrong-date save (CR-01) and the
        // duplicated card list this builder would otherwise appendChild a second time (WR-01).
        if (date !== currentDate) { done?.(true); return; }

        const runsData: RunData[] = data.status === 1 ? (data.runs || []) : [];

        if (!blankUntilLoaded) {
            if (runsData.length === 0) {
                // Skeleton blank card already matches the empty state -- nothing to populate.
                done?.(true);
                return;
            }
            // Populate the already-rendered skeleton card in place with the first run (never
            // destroys a shown.bs.modal focus already landed on it), then append the rest.
            const firstCard = openContainer!.querySelector('.run-card') as HTMLElement | null;
            if (firstCard) {
                populateRunCard(firstCard, runsData[0]);
            }
            for (let i = 1; i < runsData.length; i++) {
                renderRunCard(openContainer!, runsData[i]);
            }
            done?.(true);
            return;
        }

        buildRunsBody(runsData);
        done?.(true);
    });
}

/**
 * Date-nav step callback (Phase 262, HOME-01/02) -- the ModalDateStepper handed to shared.ts's
 * active-stepper registry from inside openRunsModal().
 */
function step(newDate: string, done: (ok: boolean) => void): void {
    // T-262-30: currentDate/newCardCounter are reassigned BEFORE the fetch is issued.
    currentDate = newDate;
    newCardCounter = 0;
    renderBodyForDate(newDate, true, done);
}

export function openRunsModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    // Phase 262 HOME-01/02: register this modal's step implementation before the modal is
    // shown, next to the existing currentDate assignment.
    setActiveStepper(date, step);

    // D-01 show-first: title/footer are computable synchronously (no field in them depends on
    // the AJAX response), so render + show BEFORE the getRunsFromDate call. P-01: bare,
    // date-free title -- the shared date-nav header owns the date.
    const title = document.getElementById('chart-edit-modal-title');
    if (title) title.textContent = 'Runs';

    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    if (footer) footer.classList.add('d-none');

    renderBodyForDate(date, false);

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
    modalInstance.show();
}

// ── Run cards ──────────────────────────────────────────────

function renderRunCard(container: HTMLElement, run: RunData | null): HTMLElement {
    const cardId = run ? 'run-card-' + run.runs_id : 'run-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 run-card';
    card.id = cardId;
    card.dataset.runsId = run ? String(run.runs_id) : '';
    card.dataset.type = 'run';

    const header = document.createElement('div');
    header.className = 'card-header d-flex justify-content-between align-items-center py-1 px-2';
    header.innerHTML = `<small class="text-primary fw-semibold">Run</small><button type="button" class="btn btn-sm btn-link text-danger p-0 js-card-delete" title="Delete">\u{1F5D1}</button>`;
    card.appendChild(header);

    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const kmVal = run?.km != null ? String(run.km) : '';
    const milesVal = run?.miles != null ? String(run.miles) : '';
    const timeVal = run?.time ?? '';
    const inclineVal = run?.incline != null ? String(run.incline) : '';

    cardBody.innerHTML = `
        <div class="row g-2 mb-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">km</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-run-km" value="${kmVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">miles</label>
                <input type="number" step="any" inputmode="decimal" class="form-control form-control-sm js-run-miles" value="${milesVal}" />
            </div>
        </div>
        <div class="row g-2">
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">time</label>
                <input type="text" class="form-control form-control-sm js-run-time" placeholder="HH:MM:SS" value="${timeVal}" />
            </div>
            <div class="col-6">
                <label class="form-label form-label-sm mb-1">incline</label>
                <input type="number" step="0.1" inputmode="decimal" class="form-control form-control-sm js-run-incline" value="${inclineVal}" />
            </div>
        </div>
    `;
    card.appendChild(cardBody);

    const kmInput = card.querySelector('.js-run-km') as HTMLInputElement;
    const milesInput = card.querySelector('.js-run-miles') as HTMLInputElement;
    attachKmMilesSync(kmInput, milesInput);

    const deleteBtn = header.querySelector('.js-card-delete') as HTMLButtonElement;
    deleteBtn.addEventListener('click', () => handleDeleteRun(card, run));

    container.appendChild(card);
    return card;
}

/**
 * Populate a skeleton run card (originally rendered with run=null) in place once the
 * getRunsFromDate response resolves — sets the real runs_id/km/miles/time/incline values on
 * the SAME DOM node (never rebuilds it) so a shown.bs.modal focus already landed on one of
 * its fields is never destroyed by this late response (D-01).
 */
function populateRunCard(card: HTMLElement, run: RunData): void {
    card.id = 'run-card-' + run.runs_id;
    card.dataset.runsId = String(run.runs_id);

    const kmInput = card.querySelector('.js-run-km') as HTMLInputElement | null;
    if (kmInput) kmInput.value = run.km != null ? String(run.km) : '';
    const milesInput = card.querySelector('.js-run-miles') as HTMLInputElement | null;
    if (milesInput) milesInput.value = run.miles != null ? String(run.miles) : '';
    const timeInput = card.querySelector('.js-run-time') as HTMLInputElement | null;
    if (timeInput) timeInput.value = run.time ?? '';
    const inclineInput = card.querySelector('.js-run-incline') as HTMLInputElement | null;
    if (inclineInput) inclineInput.value = run.incline != null ? String(run.incline) : '';

    // Re-bind delete to the now-known run — the skeleton's delete handler closed over
    // run=null (an unsaved-card removal, not a real DELETE call).
    const deleteBtn = card.querySelector('.js-card-delete') as HTMLButtonElement | null;
    if (deleteBtn) {
        const freshDeleteBtn = deleteBtn.cloneNode(true) as HTMLButtonElement;
        deleteBtn.replaceWith(freshDeleteBtn);
        freshDeleteBtn.addEventListener('click', () => handleDeleteRun(card, run));
    }
}

function attachKmMilesSync(kmInput: HTMLInputElement, milesInput: HTMLInputElement): void {
    kmInput.addEventListener('input', () => {
        if (converting) return;
        converting = true;
        const km = parseFloat(kmInput.value);
        milesInput.value = !isNaN(km) ? (km / 1.60934).toFixed(3) : '';
        converting = false;
    });

    milesInput.addEventListener('input', () => {
        if (converting) return;
        converting = true;
        const miles = parseFloat(milesInput.value);
        kmInput.value = !isNaN(miles) ? (miles * 1.60934).toFixed(3) : '';
        converting = false;
    });
}

function handleDeleteRun(card: HTMLElement, run: RunData | null): void {
    if (!window.confirm('Delete this run?')) return;

    if (!run || !run.runs_id) {
        card.remove();
        return;
    }

    post('?do=deleteRun&runsId=' + run.runs_id, {}, (data: any) => {
        if (data.status === 1) {
            card.remove();
            if (data.new_km_sum !== undefined) {
                updateChartPoint(currentDate, data.new_km_sum);
            }
            // D-09/D-10/WARNING-1: range-scoped km titlebar refresh alongside the retained
            // per-day chart-point update — `data.totals` is the FLAT km totals list.
            updateChartTotals('km', data.totals);
            notifier.success('Run deleted');
        } else {
            alert(data.message || 'Failed to delete run.');
        }
    });
}

// ── Save All ───────────────────────────────────────────────

function handleSaveAll(saveAllBtn: HTMLButtonElement): void {
    if (saveAllBtn.disabled) return;   // D-04 re-entrancy guard — Enter-to-save calls this
                                        // directly, bypassing native disabled-button click suppression
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-activity-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-activity-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    const prevError = modalEl.querySelector('.activity-inline-error');
    if (prevError) prevError.remove();

    const runCards = Array.from(document.querySelectorAll('#chart-edit-modal-body .run-card')) as HTMLElement[];

    if (runCards.length === 0) {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const runPromises: Promise<any>[] = runCards.map(card => {
        const runsIdAttr = card.dataset.runsId || '';
        const runsId = runsIdAttr !== '' ? parseInt(runsIdAttr) : null;
        const payload: Record<string, string | number> = {
            km: (card.querySelector('.js-run-km') as HTMLInputElement).value,
            miles: (card.querySelector('.js-run-miles') as HTMLInputElement).value,
            time: (card.querySelector('.js-run-time') as HTMLInputElement).value,
            incline: (card.querySelector('.js-run-incline') as HTMLInputElement).value,
        };
        if (runsId !== null && runsId > 0) payload.runs_id = runsId;
        return new Promise<any>((resolve, reject) => {
            post('?do=upsertRun&date=' + currentDate, payload, (data: any) => {
                if (data.status === 1) {
                    // WR-02 (220-REVIEW) id write-back: stamp the returned id onto THIS
                    // card's dataset as soon as its own write lands, so a retry after a
                    // partial Save-All failure (e.g. via the Enter-triggered retry path)
                    // UPDATEs the now-known row instead of re-INSERTing a duplicate.
                    if (data.runs_id !== undefined && data.runs_id !== null) {
                        card.dataset.runsId = String(data.runs_id);
                    }
                    resolve(data);
                } else {
                    reject(data.message || 'Save failed');
                }
            });
        });
    });

    Promise.all(runPromises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update runs km chart point from last run result (per-day point — unchanged; a
        // per-DAY aggregate is order-independent across cards written for the same date).
        const lastRun = results[results.length - 1];
        if (lastRun?.new_km_sum !== undefined) {
            updateChartPoint(currentDate, lastRun.new_km_sum);
        }
        // WR-02 (220-REVIEW) totals race fix: the last array-indexed result's `.totals` is NOT
        // reliably the most-recent range-scoped snapshot — Promise.all's concurrent POSTs
        // can resolve out of server-completion order, so the "last" array entry may have
        // been computed before a sibling card's write actually landed. Issue ONE dedicated
        // re-fetch via the existing fetchGraphData signal (already used by customize.ts's
        // on-demand graph reveal) now that every write has settled, instead of trusting any
        // single per-card response's totals snapshot.
        post('?do=fetchGraphData&key=km', {}, (graphResp: any) => {
            if (graphResp.success && graphResp.graph) {
                updateChartTotals('km', graphResp.graph.totals);
            } else {
                // Fall back to the pre-existing (racy but non-fatal) behavior rather than
                // leaving the titlebar stale outright.
                updateChartTotals('km', lastRun?.totals);
            }
        });

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 activity-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

// ── Chart update ───────────────────────────────────────────

function updateChartPoint(date: string, newKmSum: number | null): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_km');
    if (!chart) return;

    const ts = Date.parse(date + 'T00:00:00');
    const series = chart.series[0];
    if (!series) return;

    const point = series.points.find((p: Highcharts.Point) => p.x === ts);
    if (point) {
        //@ts-ignore
        point.update(newKmSum);
    }
}
